import React from "react";

import { IShopifyData } from "@smartrr/shared/entities/ShopifyData";
import BasicCustomerEmailFrame from "@smartrr/shared/entities/CustomerEmailConfig/components/BasicCustomerEmailFrame";
import { BodyLg, BodySm, HeadingLg } from "@smartrr/shared/entities/CustomerEmailConfig/components/Text";
import { formatTemplateVariable } from "@smartrr/shared/entities/CustomerEmailConfig/utils";
import { RoundedButton } from "@smartrr/shared/entities/CustomerEmailConfig/components/RoundedButton";
import { ShopHeaderWithLogo } from "@smartrr/shared/entities/CustomerEmailConfig/emails/CustomerEmails/components/ShopHeaderWithLogo";
import { ShopFooterWithAddress } from "@smartrr/shared/entities/CustomerEmailConfig/emails/CustomerEmails/components/ShopFooterWithAddress";
import { HaveAQuestionSection } from "../components/HaveAQuestionSection";

/**
 * Template context:
 * - `customer.firstName`
 * - `priceChange.productName`
 * - `priceChange.variantName`
 * - `priceChange.previousPrice`
 * - `priceChange.updatedPrice`
 * - `priceChange.discountedPrice`
 * - `priceChange.shopCurrency`
 * - `priceChange.lineItemCurrency`
 * - `priceChange.discount`
 * - `priceChange.sellingPlanName`
 * - `theming.colors.*`
 */
export const CustomerPriceChangeEmail = ({ shopifyData }: { shopifyData: IShopifyData }) => (
  <BasicCustomerEmailFrame
    emailTitle="Your subscription price is changing"
    emailPreviewText="Your subscription price is changing"
  >
    <ShopHeaderWithLogo shopifyData={shopifyData} />
    <HeadingLg
      data-testid="customer-price-change__header"
      style={{ color: formatTemplateVariable("theming.colors.primaryTextColor") }}
    >
      Your subscription price is changing
    </HeadingLg>
    <BodyLg
      data-testid="customer-price-change__intro-body"
      style={{ padding: "0 0 30px", color: formatTemplateVariable("theming.colors.primaryTextColor") }}
    >
      Hi {formatTemplateVariable("customer.firstName")},
    </BodyLg>
    <BodyLg
      data-testid="customer-price-change__price-change-notice-body"
      style={{ padding: "0 0 30px", color: formatTemplateVariable("theming.colors.primaryTextColor") }}
    >
      We&apos;re writing to let you know that the price of one of the products in your subscription{" "}
      {formatTemplateVariable("priceChange.sellingPlanName")} is changing.
    </BodyLg>
    <BodyLg
      data-testid="customer-price-change__effective-date-body"
      style={{ padding: "0 0 30px", color: formatTemplateVariable("theming.colors.primaryTextColor") }}
    >
      These updated prices will apply starting with your next scheduled charge.
    </BodyLg>
    <BodyLg
      data-testid="customer-price-change__products-body"
      style={{
        padding: "0 0 8px",
        fontWeight: "bold",
        color: formatTemplateVariable("theming.colors.primaryTextColor"),
      }}
    >
      Products with price changes:
    </BodyLg>

    <BodySm
      data-testid="customer-price-change__products-list"
      style={{
        padding: "10px 20px",
        color: formatTemplateVariable("theming.colors.primaryTextColor"),
      }}
    >
      <i>
        {formatTemplateVariable("priceChange.productName")}
        {`<% if (priceChange.variantName && priceChange.variantName !== "Default Title") { %>`} —{" "}
        {formatTemplateVariable("priceChange.variantName")}
        {`<% } %>`}: {formatTemplateVariable("priceChange.previousPrice")} →{" "}
        {formatTemplateVariable("priceChange.updatedPrice")}.
        <br />
        Your discounted price: {formatTemplateVariable("priceChange.discountedPrice")}
        {`<% if (priceChange.discount) { %>`} with {formatTemplateVariable("priceChange.discount")} off
        {`<% } %>`}.
      </i>
    </BodySm>

    <BodyLg
      data-testid="customer-price-change__manage-copy-body"
      style={{ padding: "0 0 30px", color: formatTemplateVariable("theming.colors.primaryTextColor") }}
    >
      You can manage your subscription at any time — skip, swap, or cancel — from your account portal.
    </BodyLg>
    <RoundedButton
      data-testid="customer-price-change__manage-subscription-cta"
      href={`https://${shopifyData.domain}/account`}
      style={{
        width: "100%",
        marginBottom: "30px",
        background: formatTemplateVariable("theming.colors.buttonBackgroundColor"),
        color: formatTemplateVariable("theming.colors.buttonTextColor"),
      }}
    >
      Manage my subscription →
    </RoundedButton>
    <HaveAQuestionSection shopifyData={shopifyData} />
    <ShopFooterWithAddress shopifyData={shopifyData} />
  </BasicCustomerEmailFrame>
);
